import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';
import { usePermissions } from '../hooks/usePermissions';
import LanguageSwitcher from './LanguageSwitcher';

export default function AdminLayout({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { getMenuLabel, getMessage } = useTranslation();
  const { hasPermission, hasAnyPermission, loading } = usePermissions();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }
  }, []);

  // Define menu items with required permissions
  const menuItems = [
    { path: '/admin/dashboard', key: 'dashboard', icon: '📊', permission: 'dashboard.view' },
    { path: '/admin/check-balance', key: 'check_balance', icon: '💰', permissions: ['mifare.view', 'obu.view', 'customer.check_balance'] },
    { path: '/admin/topup', key: 'topup', icon: '💸', permissions: ['mifare.topup', 'customer.qr_topup'] },
    { path: '/admin/manual-payment', key: 'manual_payment', icon: '🚧', permission: 'manual_payment.process' },
    { path: '/admin/users', key: 'users', icon: '👥', permission: 'users.view' },
    { path: '/admin/vehicles', key: 'vehicles', icon: '🚗', permission: 'vehicles.view' },
    { path: '/admin/mifare', key: 'mifare', icon: '💳', permission: 'mifare.view' },
    { path: '/admin/obu-management', key: 'obu_management', icon: '📡', permission: 'obu.view' },
    { path: '/admin/wim', key: 'wim', icon: '⚖️', permission: 'wim.view' },
    { path: '/admin/wim-fine-tiers', key: 'wim_fine_tiers', icon: '🧮', permission: 'settings.edit' },
    { path: '/admin/wim-truck-types', key: 'wim_truck_types', icon: '🚚', permission: 'settings.edit' },
    { path: '/admin/wim-payment', key: 'wim_payment', icon: '💸', permission: 'wim.process_payment' },
    { path: '/admin/reset-password', key: 'reset_password', icon: '🔐' }, // No permission required
    { path: '/admin/transactions', key: 'transactions', icon: '📜', permission: 'transactions.view' },
    { path: '/admin/events', key: 'events', icon: '📋', permission: 'reports.view' },
    { path: '/admin/analysis', key: 'analysis', icon: '🔍', permission: 'reports.view' },
    { path: '/admin/reports', key: 'reports', icon: '📈', permission: 'reports.view' },
    { path: '/admin/settings', key: 'settings', icon: '⚙️', permission: 'settings.view' }
  ];

  // Filter menu items based on user permissions
  const filteredMenuItems = menuItems.filter(item => {
    // If no permission required, always show
    if (!item.permission && !item.permissions) return true;

    // If single permission required
    if (item.permission) {
      const [module, action] = item.permission.split('.');
      return hasPermission(module, action);
    }

    // If multiple permissions (any of them)
    if (item.permissions) {
      return hasAnyPermission(item.permissions);
    }

    return false;
  });

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      {/* Sidebar */}
      <div className={`fixed lg:static inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 w-64 h-full flex flex-col p-4 shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-200 ease-in-out lg:translate-x-0`}>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">
            📊 {getMessage('app.title')}
          </h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <nav className="space-y-1 flex-grow overflow-y-auto">
          {loading ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              <div className="animate-pulse">Loading menu...</div>
            </div>
          ) : filteredMenuItems.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              <div className="text-sm">No menu items available</div>
            </div>
          ) : (
            filteredMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center p-3 rounded-lg transition-colors duration-200 group ${isActive(item.path)
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-l-4 border-blue-500'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="text-lg mr-3 flex-shrink-0 w-6 h-6 flex items-center justify-center text-center leading-none">{item.icon}</span>
                <span className="font-medium flex-1">{getMenuLabel(item.key)}</span>
              </Link>
            ))
          )}
        </nav>

        <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={() => {
              onLogout();
              toast.success(`👋 ${getMessage('messages.logout_success')}`);
            }}
            className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            🚪 {getMenuLabel('logout')}
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mr-4"
              >
                ☰
              </button>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                {getMessage('app.title')} - {getMessage('dashboard.title')}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <LanguageSwitcher compact={true} />

              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full cursor-pointer group"
                >
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {user?.name || 'Admin User'}
                    </div>
                    {user?.role_name && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user.role_name}
                      </div>
                    )}
                  </div>
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-sm group-hover:bg-blue-600 transition-colors">
                    <span className="text-white text-sm font-bold">👤</span>
                  </div>
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 z-50">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          onLogout();
                          toast.success(`👋 ${getMessage('messages.logout_success')}`);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                      >
                        <span className="mr-2">🚪</span>
                        {getMenuLabel('logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900 p-3 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}